import Link from 'next/link';
import { type FC, forwardRef, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@/components/avatar';
import { Button } from '@/components/buttons';
import {
  BonanzaPromoDashboardDesktopPanel,
  DashboardPanelMobile,
} from '@/components/general/Header/DashboardPanel';
import { ACCOUNT_LINKS } from '@/components/general/Header/UserBlock/helpers';
import { IconComponent } from '@/components/IconComponent';
import { SelectDeposit } from '@/components/SelectDeposit';
import { Skeleton } from '@/components/Skeleton';
import { NamesSteps } from '@/helpers/auth';
import { ThemeMode } from '@/helpers/enums';
import { Modals } from '@/helpers/modals';
import { groupMemberDocumentsRequested } from '@/helpers/user';
import { useNavigation } from '@/hooks/navigation';
import { useComponentVisible } from '@/hooks/visibility';
import { useCMSAvatar } from '@/modules/avatar';
import { Badge } from '@/modules/common/components/Badge';
import { sendOtherSignOutClickEvent } from '@/modules/googleAnalytics/events/otherEvents';
import { usePromotionsAvailability } from '@/modules/promotions';
import { useActiveBonuses } from '@/modules/promotions/components/PromotionsPage/sections/ActiveBonuses/hooks/useActiveBonuses';
import { VIPBadge } from '@/modules/vip/components/VIPBadge';
import {
  STORAGE,
  useGetCommonStore,
  useLocalStorageStore,
  useModalsStore,
} from '@/store';
import {
  PersistentFormName,
  usePersistentFormsSlice,
} from '@/store/commonStore/slices/persistant/usePersistentFormsSlice';
import { LoyaltyProgramSection } from '../DashboardPanel/LoyaltyProgramSection';
import {
  ScAccountAvatar,
  ScAccountBlock,
  ScAccountButton,
  ScAccountDropDown,
  ScAccountDropDownLinkValue,
  ScAccountInfo,
  ScDepositPanel,
  type ScDepositPanelProps,
  ScUserWelcome,
} from './styled';

const AccountBlock: FC = () => {
  const useCommonStore = useGetCommonStore();

  const resetUser = useCommonStore.use.resetUser();
  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const open = useModalsStore.use.open();
  const { setCurrentPersistentStep } = usePersistentFormsSlice();

  const { avatarImg } = useCMSAvatar();

  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const toggleOpenAccountInfo = () => {
    setIsComponentVisible(!isComponentVisible);
  };

  const handleLogout = () => {
    sendOtherSignOutClickEvent();
    useLocalStorageStore.getState().remove(STORAGE.HAS_SHOWN_LIMIT_MODAL);

    setCurrentPersistentStep(
      PersistentFormName.Registration,
      NamesSteps.PRIMARY_INFORMATION
    );
    resetUser();
  };

  const handleOpenAvatarModal = () => {
    toggleOpenAccountInfo();
    open(Modals.ChooseAvatar);
  };

  return (
    <ScAccountBlock>
      <ScAccountButton
        active={isComponentVisible}
        data-testid="user-avatar-header"
        onClick={toggleOpenAccountInfo}
        ref={openerRef}
      >
        <ScAccountAvatar>
          <Avatar {...avatarImg} />
        </ScAccountAvatar>

        <div className="arrow">
          <IconComponent name="whiteArrowRight" size="16px" />
        </div>
      </ScAccountButton>
      {isComponentVisible &&
        (isMobileDevice ? (
          <AccountInfo onClose={toggleOpenAccountInfo} />
        ) : (
          <AccountDropDown
            onOpenAvatarModal={handleOpenAvatarModal}
            onResetUser={handleLogout}
            ref={ref}
            toggleOpenAccountInfo={toggleOpenAccountInfo}
          />
        ))}
    </ScAccountBlock>
  );
};

const UserWelcome = () => {
  const useCommonStore = useGetCommonStore();

  const { t } = useTranslation('common');
  const user = useCommonStore.use.user();

  if (!user) return null;

  const { firstName, email } = user;

  return (
    <ScUserWelcome>
      <div className="welcome">
        <p>{t('greeting', { name: firstName })}</p>
        <VIPBadge />
      </div>
      <p className="email">{email}</p>
    </ScUserWelcome>
  );
};

interface AccountDropDownProps {
  onResetUser: () => void;
  onOpenAvatarModal: () => void;
  toggleOpenAccountInfo: () => void;
}

const AccountDropDown = forwardRef<HTMLDivElement, AccountDropDownProps>(
  ({ onResetUser, onOpenAvatarModal, toggleOpenAccountInfo }, ref) => {
    const useCommonStore = useGetCommonStore();

    const { t } = useTranslation('common');
    const user = useCommonStore.use.user();

    const { generateLink } = useNavigation();

    const activeBonuses = useActiveBonuses();
    const activeBonusesCount = activeBonuses.length;

    const hasDocumentsRequested = useMemo(
      () => groupMemberDocumentsRequested(user),
      [user]
    );

    return (
      <ScAccountDropDown ref={ref}>
        <UserWelcome />
        {user && <div className="divider" />}

        <ul>
          {ACCOUNT_LINKS.map(({ name, link }) => (
            <li key={name}>
              <Link
                href={generateLink(link)}
                onClick={toggleOpenAccountInfo}
                prefetch={false}
              >
                <ScAccountDropDownLinkValue
                  showNotification={
                    hasDocumentsRequested && name === 'profileInfo'
                  }
                >
                  {t(`accountLinks.${name}`)}
                  {name === 'bonusCabinet' && !!activeBonusesCount && (
                    <Badge>{activeBonusesCount}</Badge>
                  )}
                </ScAccountDropDownLinkValue>
              </Link>
            </li>
          ))}
          <li>
            <button onClick={onOpenAvatarModal} type="button">
              {t('accountLinks.chooseAvatar')}
            </button>
          </li>
          <li>
            <button
              data-testid="logout-button"
              onClick={onResetUser}
              type="button"
            >
              {t('button.signOut')}
            </button>
          </li>
        </ul>
      </ScAccountDropDown>
    );
  }
);

interface IAccountInfoProps {
  onClose: () => void;
}

export const AccountInfo: FC<IAccountInfoProps> = ({ onClose }) => {
  const open = useModalsStore.use.open();
  const { avatarImg } = useCMSAvatar();
  const useCommonStore = useGetCommonStore();
  const isAuthenticated = useCommonStore.use.isAuthenticated();

  const handleOpenAvatarModal = () => {
    onClose();
    open(Modals.ChooseAvatar);
  };

  return (
    <ScAccountInfo>
      {isAuthenticated && (
        <div className="user-info">
          <ScAccountAvatar isMobile onClick={handleOpenAvatarModal}>
            <Avatar {...avatarImg} />
          </ScAccountAvatar>
          <UserWelcome />
        </div>
      )}

      <DashboardPanelMobile onLinkClick={onClose} />
    </ScAccountInfo>
  );
};

export const UserPanel = () => {
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const { isBonanzaPromoAvailable } = usePromotionsAvailability();

  return (
    <>
      <Suspense fallback={<Skeleton.Rect height={48} rounded width={100} />}>
        {isBonanzaPromoAvailable && <BonanzaPromoDashboardDesktopPanel />}
        <LoyaltyProgramSection />
      </Suspense>
      <DepositPanel />
      {!isMobileDevice && <AccountBlock />}
    </>
  );
};

export const DepositPanel: FC<
  ScDepositPanelProps & {
    boldValue?: boolean;
    onLinkClick?: () => void;
  }
> = ({
  mobile,
  noPadding,
  onLinkClick,
  reducedSizeOnSmallScreen,
  ...props
}) => {
  const { t } = useTranslation('common');
  const open = useModalsStore.use.open();

  const handleDepositClick = () => {
    open(Modals.Deposit);
    onLinkClick && onLinkClick();
  };

  return (
    <ScDepositPanel
      blackBg={props.blackBg}
      mobile={mobile}
      noPadding={noPadding}
      reducedSizeOnSmallScreen={reducedSizeOnSmallScreen}
    >
      <SelectDeposit
        inset="calc(100% + 12px) 0 auto 0"
        isDepositPanel
        isInputAddon
        mode={ThemeMode.Dark}
        reducedSizeOnSmallScreen={reducedSizeOnSmallScreen}
        transparent
        {...props}
      />
      <div className="cashier-button">
        <Button
          onClick={handleDepositClick}
          secondary
          small
          testId="deposit-button"
        >
          {t('button.deposit')}
        </Button>
      </div>
    </ScDepositPanel>
  );
};
